import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, NgForm, Validators } from '@angular/forms';

import { get } from 'lodash';

import { CommonRegEx } from '@nrwl/common-lib';

import {
  IComponentStateHistory,
  IValueChangedEvent,
  IWizardContentComponent
} from '../../../wizard.content';
import { SyslogConfigWizardService } from '../../../services/syslog/syslog.configuration.service';

@Component({
  selector: 'syslog-configuration-html',
  templateUrl: './syslog.configuration.template.html',
  styleUrls: ['../syslog.configuration.style.scss']
})
export class SyslogConfigurationComponent implements IWizardContentComponent, OnInit {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() step: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() subStep: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() wizardCtrl: any;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() previousOutput: any;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm') form: NgForm;

  host: string;
  port: string;
  identifier: string;
  enabled = false;
  severity: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any;
  protocol: string;
  logLevelList = [
    {
      id: 'emergency',
      value: 'Emergency'
    },
    {
      id: 'alert',
      value: 'Alert'
    },
    {
      id: 'critical',
      value: 'Critical'
    },
    {
      id: 'error',
      value: 'Error'
    },
    {
      id: 'warning',
      value: 'Warning'
    },
    {
      id: 'notice',
      value: 'Notice'
    },
    {
      id: 'informational',
      value: 'Informational'
    },
    {
      id: 'debug',
      value: 'Debug'
    }
  ];
  protocolList = [
    {
      id: 'tcp',
      value: 'TCP'
    },
    {
      id: 'udp',
      value: 'UDP'
    },
    {
      id: 'ssl-tcp',
      value: 'SSL-TCP'
    }
  ];
  hide = true;
  showFusionLogLevel = false;
  loadMessage = 'Saving Syslog configuration';

  constructor(private wizardService: SyslogConfigWizardService) {}

  ngOnInit() {
    this.step.form = this.form;
    this.params = [];
    //Default Value
    this.severity = 'alert';
    this.protocol = 'tcp';
    this.wizardCtrl.activatedRoute.queryParams.subscribe(value => {
      if (value.source && value.source === 'FA') {
        this.severity = 'debug';
        this.showFusionLogLevel = true;
      }
    });
    if (this.wizardService.editMode) {
      this.resetOutput();
      this.loadMessage = 'Updating Syslog configuration';
    } else {
      this.protocolUpdated();
      this.severityUpdated();
      this.resetValue();
    }
  }

  resetOutput(): void {
    this.host = get(this.step, 'output.host');
    this.port = get(this.step, 'output.port');
    this.identifier = get(this.step, 'output.identifier');
    this.protocol = get(this.step, 'output.protocol');
    this.severity = get(this.step, 'output.severity_filter');
    this.enabled = get(this.step.parent, 'defaultParamsById.enabled');
    this.enabledUpdated();
  }

  enabledUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'enableInput',
      key: 'enabled',
      newValue: this.enabled,
      isValid: true
    });
  }

  hostUpdated(): void {
    // Dynamically add validators to form control
    this.form.controls.host.setValidators([
      Validators.required,
      Validators.maxLength(80),
      this.validateHost.bind(this)
    ]);
    this.form.controls.host.updateValueAndValidity();

    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'hostInput',
      key: 'host',
      newValue: this.host,
      isValid: this.form.controls.host.valid
    });
  }

  portUpdated(): void {
    // Dynamically add validators to form control
    this.form.controls.port.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(65535)
    ]);
    this.form.controls.port.updateValueAndValidity();

    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'portInput',
      key: 'port',
      newValue: this.port,
      isValid: this.form.controls.port.valid
    });
  }

  identifierUpdated(): void {
    // Dynamically add validators to form control
    this.form.controls.identifier.setValidators([
      Validators.required,
      Validators.maxLength(50),
      this.validateTitle.bind(this)
    ]);
    this.form.controls.identifier.updateValueAndValidity();

    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'identifierInput',
      key: 'identifier',
      newValue: this.identifier,
      isValid: this.form.controls.identifier.valid
    });
  }

  severityUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'severityInput',
      key: 'severity_filter',
      newValue: this.severity,
      isValid: true
    });
  }

  protocolUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'protocolInput',
      key: 'protocol',
      newValue: this.protocol,
      isValid: true
    });
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateHost(control: AbstractControl): any {
    if (control.value !== undefined && control.value !== null && control.value !== '') {
      if (!control.value.match(CommonRegEx.DNS_NAME)) {
        return { matchPattern: true };
      }
    }
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateTitle(control: AbstractControl): any {
    if (control.value !== undefined && control.value !== null && control.value !== '') {
      if (
        !control.value.match(/^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/)
      ) {
        return { matchPattern: true };
      }
    }
  }
  resetValue(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'identifierInput',
      key: 'identifier',
      newValue: '',
      isValid: false
    });
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'portInput',
      key: 'port',
      newValue: '',
      isValid: false
    });
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'hostInput',
      key: 'host',
      newValue: '',
      isValid: false
    });
  }
}
